import type { ChangeRequest } from 'models';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes/routes';
import {
  Button,
  CheckboxInput,
  Col,
  Form,
  type FormInstance,
  Link,
  PasswordInput,
  Row,
  Section,
  Skeleton,
  Text,
  TextInput,
} from 'ui';
import { isPasswordValid } from 'common/validators';
import { useChangeRequestsQuery } from '../../../../queries';
import { type PrimaryOrganizationOption, PrimaryOrganizationSelect } from '../../primary-organization-select';

type P = {
  changeRequestId?: string;
  form: FormInstance<SignUpFormValues>;
  initialOrganization?: PrimaryOrganizationOption;
  invitationToken?: string;
  loading: boolean;
  onSubmit: (data: SignUpFormValues) => void;
};

const getInitialValues = ({
  changeRequestData,
  initialOrganization,
}: {
  changeRequestData?: ChangeRequest;
  initialOrganization?: PrimaryOrganizationOption;
}): Partial<SignUpFormValues> => ({
  email: changeRequestData?.meta.invitationEmail,
  firstName: changeRequestData?.meta.firstName,
  lastName: changeRequestData?.meta.lastName,
  organization: initialOrganization,
});

export type SignUpFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  organization?: PrimaryOrganizationOption;
  password: string;
};

const SignUpForm = ({ changeRequestId, form, initialOrganization, invitationToken, loading, onSubmit }: P) => {
  const { t } = useTranslation();

  const {
    data: changeRequestData,
    isFetching,
    isLoading,
  } = useChangeRequestsQuery({
    changeRequestId,
    invitationToken,
  });

  const initialValues = getInitialValues({ changeRequestData, initialOrganization });

  const onFinish = (data: SignUpFormValues) => {
    onSubmit(data);
  };

  return (
    <div className="sign-up-form">
      {isLoading && isFetching ? (
        <Skeleton active className="sign-up-form__loading" paragraph={{ rows: 8 }} />
      ) : (
        <Form<SignUpFormValues> form={form} initialValues={initialValues} onFinish={onFinish}>
          <Row gutter="ms">
            {initialOrganization ? (
              <Col span={24}>
                <Form.Item<SignUpFormValues>
                  label="Select your organization"
                  name="organization"
                  rules={[{ required: true }]}
                >
                  <PrimaryOrganizationSelect />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={12}>
              <TextInput
                input={{
                  autoComplete: 'given-name',
                }}
                item={{
                  label: 'First name',
                  name: 'firstName',
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                    },
                  ],
                }}
              />
            </Col>
            <Col span={12}>
              <TextInput
                input={{
                  autoComplete: 'family-name',
                }}
                item={{
                  label: 'Last name',
                  name: 'lastName',
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                    },
                  ],
                }}
              />
            </Col>
          </Row>
          <TextInput
            input={{
              autoComplete: 'email',
            }}
            item={{
              label: 'Email',
              name: 'email',
              rules: [
                {
                  type: 'email',
                },
                {
                  required: true,
                  whitespace: true,
                },
              ],
            }}
          />
          <PasswordInput
            item={{
              label: 'Password',
              name: 'password',
              rules: [
                {
                  required: true,
                  whitespace: true,
                },
                () => ({
                  message: '',
                  // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                  async validator(_, value) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    if (!isPasswordValid(value)) {
                      throw new Error('Invalid password');
                    }
                  },
                }),
              ],
            }}
            showValidation
          />
          <CheckboxInput
            item={{
              label: (
                <Text>
                  {t('I agree with')}{' '}
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases */}
                  <a href={import.meta.env.VITE_APP_TERMS_URL} rel="noopener noreferrer" target="_blank">
                    {t('Terms')}
                  </a>{' '}
                  {t('and')}{' '}
                  <Link rel="noopener noreferrer" target="_blank" to={routes.privacyPolicy}>
                    {t('Privacy Policy')}
                  </Link>
                </Text>
              ),
              name: 'termsAgreement',
              rules: [
                {
                  message: t('Error: Accepting Terms & Conditions is required'),
                  // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                  async validator(_, value) {
                    if (!value) {
                      throw new Error(t('Error: Accepting Terms & Conditions is required'));
                    }
                  },
                },
              ],
              valuePropName: 'checked',
            }}
          />
          <Section lineBottom={false} lineTop={false} paddingTop={false}>
            <Button block htmlType="submit" id="create-account" loading={loading} size="large" type="primary">
              Sign up
            </Button>
          </Section>
        </Form>
      )}
    </div>
  );
};

export default SignUpForm;
